import { useState, useCallback, useEffect, useMemo } from 'react';
import { getFirebaseAuth } from '../firebase';
import type { UserPreferences } from '../types/preferences';
import { effectiveFlags, type FlagKey, type LimitKey } from '../entitlements';

export interface OrgMembership {
  orgId: string;
  // Must mirror VALID_ROLES in server/src/routes/admin.ts (5 tiers,
  // viewer → admin). Older versions of this type left out 'viewer' and
  // 'commenter', which silently hid the commenter role from any client
  // check that narrowed on the union.
  role: 'viewer' | 'commenter' | 'user' | 'manager' | 'admin';
  orgName?: string;
  modules?: Record<string, boolean>;
  /** `organizations/{orgId}.limits`: numeric entitlements for THIS org
   *  (never merged across orgs). See src/entitlements.ts. */
  limits?: Record<string, number>;
  /** Admin-set country allow-list for this org. Null / empty means
   *  unrestricted. Used by the demo-permission flow to lock the UI to
   *  one or more countries. */
  demoCountries?: string[] | null;
}

export interface UserProfile {
  username: string;
  email: string;
  // Must mirror VALID_ROLES in server/src/routes/admin.ts (5 tiers,
  // viewer → admin). Older versions of this type left out 'viewer' and
  // 'commenter', which silently hid the commenter role from any client
  // check that narrowed on the union.
  role: 'viewer' | 'commenter' | 'user' | 'manager' | 'admin';
  orgId: string | null;
  forcePasswordChange: boolean;
  memberships: OrgMembership[];
  phoneCountryCode: string | null;
  phoneNumber: string | null;
  /** Null when the user has never completed the Preferences modal — the
   *  AuthGate uses that as its "first-run" signal to open the modal
   *  before the daily briefing fires. */
  preferences: UserPreferences | null;
}

export function useUserProfile() {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);

  const getAuthToken = async (): Promise<string | null> => {
    try {
      const auth = await getFirebaseAuth();
      const user = auth.currentUser;
      if (!user) return null;
      return await user.getIdToken();
    } catch {
      return null;
    }
  };

  // NOTE: fetchProfile is intentionally NOT dependent on selectedOrgId.
  // Earlier versions had it in the deps array, which caused the entire
  // profile to be re-fetched on every dropdown change, and stale in-flight
  // responses could reset `selectedOrgId` back to the user's default org —
  // making "switch org" feel like a no-op. Using a functional setter for
  // the default lets us read the latest selectedOrgId without re-running
  // the fetch when it changes.
  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getAuthToken();
      if (!token) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }

      const response = await fetch('/api/user/profile', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }

      const data = await response.json();
      const profileData: UserProfile = {
        username: data.username || '',
        email: data.email || '',
        role: data.role || 'user',
        orgId: data.orgId || null,
        forcePasswordChange: data.forcePasswordChange || false,
        memberships: data.memberships || [],
        phoneCountryCode: data.phoneCountryCode || null,
        phoneNumber: data.phoneNumber || null,
        preferences: data.preferences || null,
      };

      setProfile(profileData);

      // Initialise selectedOrgId only if it hasn't been set yet. Functional
      // setter sees the latest value without coupling fetchProfile to it.
      // Prefer the profile's primary org; fall back to the first membership.
      setSelectedOrgId((prev) => {
        if (prev) return prev;
        return profileData.orgId || profileData.memberships[0]?.orgId || null;
      });
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch profile on mount only — never on selectedOrgId changes.
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Get the role and modules for the currently selected org
  const currentOrg = profile?.memberships.find(m => m.orgId === selectedOrgId);
  const currentRole = currentOrg?.role || profile?.role || 'user';
  const modules = currentOrg?.modules || {};

  // Merge modules across ALL orgs (OR logic: if any org enables a module,
  // it's enabled), then apply the catalog's `requires` so a child flag
  // whose parent is off reads as off, exactly as the server evaluates it.
  // Memoised on the profile so consumers can key effects and Puck's
  // editor data on it without a new object every render.
  const mergedModules: Record<string, boolean> = useMemo(() => {
    const rawMerged: Record<string, boolean> = {};
    for (const m of profile?.memberships || []) {
      for (const [key, value] of Object.entries(m.modules || {})) {
        if (value) rawMerged[key] = true;
      }
    }
    return effectiveFlags(rawMerged);
  }, [profile]);

  /** Is this entitlement on for the user (any org, parents on)? The one
   *  question every "should this control show" check asks. */
  const can = (key: FlagKey): boolean => mergedModules[key] === true;

  /** The limit ONE org holds, or 0 when unset. Limits are per org, so the
   *  caller names the org a write lands in. */
  const limitFor = (orgId: string | null | undefined, key: LimitKey): number => {
    const v = profile?.memberships.find((m) => m.orgId === orgId)?.limits?.[key];
    return typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0;
  };

  // Effective demo-country allow-list. Mirrors the server's Rule A: if
  // ANY of the user's orgs has no `demoCountries` set, they are
  // unrestricted (returns null). Otherwise returns the union of allowed
  // countries across their demo-scoped orgs.
  //
  // `isDemoScoped` is the one-liner UI gate — hide / lock features
  // when it's true. Memos aren't necessary; the arrays are tiny.
  let allowedCountries: string[] | null = null;
  if (profile) {
    const members = profile.memberships || [];
    const anyUnrestricted = members.some((m) => !m.demoCountries || m.demoCountries.length === 0);
    if (!anyUnrestricted && members.length > 0) {
      const union = new Set<string>();
      for (const m of members) for (const c of (m.demoCountries || [])) union.add(c);
      allowedCountries = Array.from(union);
    }
  }
  const isDemoScoped = Array.isArray(allowedCountries) && allowedCountries.length > 0;

  // Helper to check if user can comment. Server rule (comments.ts) is the
  // `comments` entitlement plus "anyone except viewer" — keep the client
  // in sync so we don't hide the button for commenter / user roles that
  // the API would accept posts from, and never show it to an org whose
  // comment routes answer 403.
  const canComment = can('comments') && !!currentRole && currentRole !== 'viewer';

  /** Persist a new preferences payload for the signed-in user and
   *  optimistically update local state. Callers (PreferencesModal) can
   *  rely on `profile.preferences` reflecting the new value immediately
   *  — the fetchProfile hook does NOT refetch on nav. Throws on failure
   *  so the modal can surface the error. */
  const savePreferences = useCallback(async (prefs: UserPreferences): Promise<UserPreferences> => {
    const token = await getAuthToken();
    if (!token) throw new Error('Not authenticated');
    const res = await fetch('/api/user/preferences', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(prefs),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Failed to save preferences (${res.status})`);
    }
    const data = await res.json() as { preferences: UserPreferences };
    setProfile((prev) => prev ? { ...prev, preferences: data.preferences } : prev);
    return data.preferences;
  }, []);

  return {
    profile,
    loading,
    error,
    fetchProfile,
    canComment,
    selectedOrgId,
    setSelectedOrgId,
    currentRole,
    modules,
    mergedModules,
    can,
    limitFor,
    allowedCountries,
    isDemoScoped,
    savePreferences,
  };
}